import React, { useMemo, useState } from 'react';
import {
  DescriptionText,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  SectionSeparator,
  Surface,
  VerticalStack,
  WizardWorkspace,
} from '@workspace/silk-ui';
import {
  DollarSign,
  Database,
  Info,
  MapPin,
  Route,
  Settings2,
} from 'lucide-react';
import type { MapSource } from '@/lib/mapping-api';
import {
  buildMinimumBuildCostBandLabel,
  type RoutePricingBuildTypeRateFormValue,
} from '@/features/commerce-admin/routePricingRateController';

export type AccessPointLayerFormState = {
  layerKey: string;
  name: string;
  accessPointType: string;
  purpose: string;
  status: string;
  mappingSourceId: string;
  defaultBuildTypeRates: RoutePricingBuildTypeRateFormValue[];
  metadataJSON: string;
};

export interface AccessPointsWizardEditorProps {
  isOpen: boolean;
  editingId: string | null;
  layerForm: AccessPointLayerFormState;
  availableMapSources: MapSource[];
  setLayerForm: React.Dispatch<React.SetStateAction<AccessPointLayerFormState>>;
  onSave: () => Promise<void>;
  onClose: () => void;
  saving: boolean;
}

function FormField({ label, children, htmlFor }: { label: string; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-xs font-black uppercase tracking-widest text-muted-foreground/80">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'h-12 w-full rounded-lg border border-border/60 bg-background px-4 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';
const selectCls = `${inputCls} cursor-pointer`;
const textareaCls = 'min-h-36 w-full rounded-lg border border-border/60 bg-background px-4 py-3 font-mono text-xs font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:ring-4 focus:ring-primary/5';

function RateRows({
  rates,
  onChange,
  addLabel,
}: {
  rates: RoutePricingBuildTypeRateFormValue[];
  onChange: (next: RoutePricingBuildTypeRateFormValue[]) => void;
  addLabel: string;
}) {
  return (
    <VerticalStack gap={3}>
      {rates.map((rate, index) => (
        <div key={rate.rowId}>
          <HorizontalStack gap={3} className="grid grid-cols-1 items-end sm:grid-cols-[1fr_8rem_1fr_1fr_auto]">
            <FormField htmlFor={`${rate.rowId}-build-type`} label={index === 0 ? 'Build Type' : `Build Type ${index + 1}`}>
              <input
                id={`${rate.rowId}-build-type`}
              className={inputCls}
              value={rate.buildType}
              onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, buildType: event.target.value } : current)))}
              placeholder="aerial"
            />
          </FormField>
            <FormField htmlFor={`${rate.rowId}-currency`} label={index === 0 ? 'Currency' : `Currency ${index + 1}`}>
              <input id={`${rate.rowId}-currency`} className={inputCls} value={rate.currency} maxLength={3} onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, currency: event.target.value.toUpperCase() } : current)))} placeholder="USD" />
            </FormField>
            <FormField htmlFor={`${rate.rowId}-price-per-meter`} label={index === 0 ? 'Price / m' : `Price / m ${index + 1}`}>
              <input
                id={`${rate.rowId}-price-per-meter`}
                inputMode="decimal"
              className={inputCls}
              value={rate.pricePerMeter}
              onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, pricePerMeter: event.target.value } : current)))}
              placeholder="10"
            />
          </FormField>
            <FormField htmlFor={`${rate.rowId}-minimum-build-cost`} label={index === 0 ? 'Minimum Build Cost' : `Minimum ${index + 1}`}>
              <input id={`${rate.rowId}-minimum-build-cost`} inputMode="decimal" className={inputCls} value={rate.minimumBuildCost} onChange={event => onChange(rates.map((current, currentIndex) => (currentIndex === index ? { ...current, minimumBuildCost: event.target.value } : current)))} placeholder="0.00" />
          </FormField>
          <button
            type="button"
            aria-label={`Remove ${rate.buildType || `rate ${index + 1}`}`}
            onClick={() => onChange(rates.filter((_, currentIndex) => currentIndex !== index))}
            className="inline-flex h-10 items-center rounded-md border border-border/60 px-3 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-muted/30"
          >
            Remove
          </button>
        </HorizontalStack>
        {buildMinimumBuildCostBandLabel(rate) && <DescriptionText className="mt-1 text-[11px]">{buildMinimumBuildCostBandLabel(rate)}</DescriptionText>}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rates, { rowId: `route-rate-${Date.now()}`, buildType: '', currency: 'USD', pricePerMeter: '', minimumBuildCost: '' }])}
        className="inline-flex h-10 w-fit items-center gap-2 rounded-md border border-border/60 bg-background px-4 text-[11px] font-black uppercase tracking-tight text-foreground transition-colors hover:bg-muted/30"
      >
        {addLabel}
      </button>
    </VerticalStack>
  );
}

export function AccessPointsWizardEditor({
  isOpen,
  editingId,
  layerForm,
  availableMapSources,
  setLayerForm,
  onSave,
  onClose,
  saving,
}: AccessPointsWizardEditorProps) {
  const [activeTabId, setActiveTabId] = useState('identity');

  const tabs = useMemo(() => [
    { id: 'identity', label: 'Identity', icon: <Info className="h-4 w-4" /> },
    { id: 'routing', label: 'Routing', icon: <Route className="h-4 w-4" /> },
    { id: 'pricing', label: 'Pricing', icon: <DollarSign className="h-4 w-4" /> },
    { id: 'source', label: 'Map Source', icon: <MapPin className="h-4 w-4" /> },
    { id: 'advanced', label: 'Advanced', icon: <Settings2 className="h-4 w-4" /> },
  ], []);

  const summaryItems = useMemo(() => [
    { label: 'Name', value: layerForm.name || 'Not set', complete: Boolean(layerForm.name) },
    { label: 'Key', value: layerForm.layerKey || 'Not set', complete: Boolean(layerForm.layerKey) },
    { label: 'Purpose', value: layerForm.purpose, complete: true },
    { label: 'Defaults', value: layerForm.defaultBuildTypeRates.length > 0 ? `${layerForm.defaultBuildTypeRates.length} build rates` : 'Not set', complete: layerForm.defaultBuildTypeRates.length > 0 },
    {
      label: 'Source',
      value: availableMapSources.find(source => source.id === layerForm.mappingSourceId)?.name || 'Not set',
      complete: Boolean(layerForm.mappingSourceId),
    },
  ], [availableMapSources, layerForm.defaultBuildTypeRates.length, layerForm.layerKey, layerForm.mappingSourceId, layerForm.name, layerForm.purpose]);

  return (
    <WizardWorkspace
      isOpen={isOpen}
      title={editingId ? 'Edit Access Point Layer' : 'New Access Point Layer'}
      subtitle="Tenant-owned fiber anchors for commerce road-distance estimates."
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      onSave={onSave}
      onClose={onClose}
      isSaving={saving}
      saveLabel={editingId ? 'Update' : 'Create'}
      summaryItems={summaryItems}
    >
      {activeTabId === 'identity' && (
        <VerticalStack gap={4}>
          <FormField label="Layer Name">
            <input
              className={inputCls}
              value={layerForm.name}
              onChange={event => setLayerForm(current => ({ ...current, name: event.target.value }))}
              placeholder="Business fiber access points"
            />
          </FormField>
          <FormField label="Layer Key">
            <input
              className={inputCls}
              value={layerForm.layerKey}
              onChange={event => setLayerForm(current => ({ ...current, layerKey: event.target.value }))}
              placeholder="business-fiber-access"
            />
          </FormField>
          <HorizontalStack gap={4} className="grid grid-cols-1 sm:grid-cols-2">
            <FormField label="Status">
              <select
                className={selectCls}
                value={layerForm.status}
                onChange={event => setLayerForm(current => ({ ...current, status: event.target.value }))}
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </FormField>
            <FormField label="Access Point Type">
              <select
                className={selectCls}
                value={layerForm.accessPointType}
                onChange={event => setLayerForm(current => ({ ...current, accessPointType: event.target.value }))}
              >
                <option value="fiber">Fiber</option>
                <option value="tap">Tap</option>
                <option value="handoff">Handoff</option>
                <option value="pop">POP</option>
                <option value="other">Other</option>
              </select>
            </FormField>
          </HorizontalStack>
        </VerticalStack>
      )}

      {activeTabId === 'routing' && (
        <VerticalStack gap={5}>
          <FormField label="Purpose">
            <select
              className={selectCls}
              value={layerForm.purpose}
              onChange={event => setLayerForm(current => ({ ...current, purpose: event.target.value }))}
            >
              <option value="business_quote_estimate">Business Quote Estimate</option>
              <option value="serviceability">Serviceability</option>
              <option value="install_feasibility">Install Feasibility</option>
            </select>
          </FormField>
          <Surface className="p-4">
            <PropertyGrid columns={2}>
              <PropertyItem label="Runtime owner" value="svc-commerce" />
              <PropertyItem label="Route engine" value="svc-routing" />
              <PropertyItem label="Selection" value="Nearest by road distance" />
              <PropertyItem label="Layer status" value={layerForm.status} />
            </PropertyGrid>
          </Surface>
        </VerticalStack>
      )}

      {activeTabId === 'pricing' && (
        <VerticalStack gap={5}>
          <DescriptionText className="text-xs">
            Define the fallback currency, price per meter, and minimum build cost by build type for route segments outside a pricing-enabled serviceability zone.
          </DescriptionText>
          <RateRows
            rates={layerForm.defaultBuildTypeRates}
            onChange={next => setLayerForm(current => ({ ...current, defaultBuildTypeRates: next }))}
            addLabel="Add default rate"
          />
        </VerticalStack>
      )}

      {activeTabId === 'source' && (
        <VerticalStack gap={5}>
          <Surface className="p-4">
            <HorizontalStack gap={3} className="items-start">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/30">
                <Database className="h-5 w-5 text-muted-foreground" />
              </div>
              <VerticalStack gap={1}>
                <p className="text-sm font-black text-foreground">Bind routing source</p>
                <DescriptionText className="text-xs">
                  Choose the tenant map source that owns the fiber access-point geometry. Commerce quote estimates and the sales map will both route against this shared source.
                </DescriptionText>
              </VerticalStack>
            </HorizontalStack>
          </Surface>

          <FormField label="Map Source">
            <select
              className={selectCls}
              value={layerForm.mappingSourceId}
              onChange={event => setLayerForm(current => ({ ...current, mappingSourceId: event.target.value }))}
            >
              <option value="">Select a map source</option>
              {availableMapSources.map(source => (
                <option key={source.id} value={source.id}>
                  {source.name || source.id}
                </option>
              ))}
            </select>
          </FormField>

          <Surface className="p-4">
            <PropertyGrid columns={2}>
              <PropertyItem label="Shared geometry owner" value="svc-mapping" />
              <PropertyItem label="Route consumer" value="svc-commerce + /sales" />
              <PropertyItem label="Source scope" value={availableMapSources.find(source => source.id === layerForm.mappingSourceId)?.share_scope || 'Not set'} />
              <PropertyItem label="Source kind" value={availableMapSources.find(source => source.id === layerForm.mappingSourceId)?.source_kind || 'Not set'} />
            </PropertyGrid>
          </Surface>
        </VerticalStack>
      )}

      {activeTabId === 'advanced' && (
        <VerticalStack gap={5}>
          <FormField label="Layer Metadata">
            <textarea
              className={textareaCls}
              value={layerForm.metadataJSON}
              onChange={event => setLayerForm(current => ({ ...current, metadataJSON: event.target.value }))}
            />
          </FormField>
          <SectionSeparator />
          <DescriptionText className="text-xs">
            Point geometry is managed centrally in map sources. Update the selected source in the Map Sources workspace when the access-point dataset changes.
          </DescriptionText>
        </VerticalStack>
      )}
    </WizardWorkspace>
  );
}
